/**
 * Sons signature des 6 maîtres mages (synthèse Tone.js pure, ~30KB total).
 *
 * Voix Vesperin (anti-Disney) :
 *   - feedback correct = son normal du maître
 *   - feedback incorrect = pitch -3 semitones + lowpass 1500Hz + gain 0.7
 *     → pensif, sombre, PAS punitif (pas de buzzer game-show).
 *
 * Chaque patch dure ≤ 600ms (sync visual feedback motion.feedbackVisualMs).
 * Les voix utilisent dispose() après decay — pas de leak AudioContext.
 *
 * Sources :
 *   - wiki/lore/voix-vesperin.md (registre sonore anti-Disney)
 *   - wiki/lore/jeux-vs-maitres-mages.md (mapping master → famille de jeux)
 */
const DEFAULT_PITCH = 0;
const DEFAULT_FILTER_HZ = 12000; // ouvert (~ pas filtré)
const DEFAULT_GAIN = 1;
/**
 * Joue le son signature du maître. Promesse résolue à la fin du decay
 * (≤ 600ms typique).
 *
 * Si l'engine n'est pas initialisé, no-op silencieux (caller doit appeler
 * `engine.init()` avant — c'est la responsabilité de la couche polish).
 */
export async function playSignatureSound(engine, master, options = {}) {
    if (!engine.isInitialized())
        return;
    const Tone = engine.getTone();
    const out = engine.getMasterOutput();
    const pitch = options.pitch ?? DEFAULT_PITCH;
    const cutoff = options.filterCutoff ?? DEFAULT_FILTER_HZ;
    const gainAtt = options.gain ?? DEFAULT_GAIN;
    // Filter post-voix → master. Un seul lowpass partagé par voix temporaire.
    const filter = new Tone.Filter({ type: 'lowpass', frequency: cutoff, Q: 0.7 }).connect(out);
    const gainNode = new Tone.Gain(gainAtt).connect(filter);
    try {
        await playPatch(Tone, master, pitch, gainNode);
    }
    finally {
        // Cleanup nodes après decay (Tone gère interne via dispose)
        setTimeout(() => {
            try {
                gainNode.dispose();
                filter.dispose();
            }
            catch {
                /* ignored */
            }
        }, 800);
    }
}
/**
 * Streak stinger — déclenchée à 3+ correct consécutif.
 * Pluck arpège ascendant (feel "lumineux ouvert", indépendant du master).
 */
export async function playStreakStinger(engine) {
    if (!engine.isInitialized())
        return;
    const Tone = engine.getTone();
    const out = engine.getMasterOutput();
    const synth = new Tone.PluckSynth({ attackNoise: 0.6, dampening: 5000, resonance: 0.85 }).connect(out);
    const now = Tone.now();
    const notes = ['D5', 'F5', 'A5', 'D6'];
    notes.forEach((n, i) => synth.triggerAttack(n, now + i * 0.06));
    await waitMs(420);
    setTimeout(() => {
        try {
            synth.dispose();
        }
        catch {
            /* ignored */
        }
    }, 200);
}
/**
 * PB stinger — déclenchée sur personal best.
 * MetalSynth avec reverb long (halo doré).
 */
export async function playPBStinger(engine) {
    if (!engine.isInitialized())
        return;
    const Tone = engine.getTone();
    const out = engine.getMasterOutput();
    const reverb = new Tone.Reverb({ decay: 3, wet: 0.55 }).connect(out);
    const synth = new Tone.MetalSynth({
        envelope: { attack: 0.005, decay: 0.4, release: 0.6 },
        harmonicity: 5,
        modulationIndex: 12,
        resonance: 4500,
        octaves: 1.2,
    }).connect(reverb);
    synth.triggerAttackRelease('A4', '8n', Tone.now());
    await waitMs(550);
    setTimeout(() => {
        try {
            synth.dispose();
            reverb.dispose();
        }
        catch {
            /* ignored */
        }
    }, 1500);
}
async function playPatch(Tone, master, pitchSemitones, output) {
    const now = Tone.now();
    const shift = (note) => Tone.Frequency(note).transpose(pitchSemitones).toNote();
    switch (master) {
        case 'air': {
            // Inori — cloches bambou : MetalSynth + reverb hall
            const reverb = new Tone.Reverb({ decay: 4, wet: 0.4 }).connect(output);
            const synth = new Tone.MetalSynth({
                envelope: { attack: 0.001, decay: 0.3, release: 0.4 },
                harmonicity: 3.1,
                modulationIndex: 8,
                resonance: 2800,
                octaves: 1.0,
            }).connect(reverb);
            synth.triggerAttackRelease(shift('E5'), '16n', now);
            await waitMs(550);
            setTimeout(() => {
                synth.dispose();
                reverb.dispose();
            }, 1200);
            break;
        }
        case 'feu': {
            // Embra — forge braise : kick MembraneSynth + noise burst
            const kick = new Tone.MembraneSynth({
                pitchDecay: 0.04,
                octaves: 4,
                envelope: { attack: 0.001, decay: 0.18, release: 0.1 },
            }).connect(output);
            const noiseEnv = new Tone.AmplitudeEnvelope({ attack: 0.001, decay: 0.08, sustain: 0, release: 0.05 }).connect(output);
            const noise = new Tone.Noise('pink').connect(noiseEnv);
            kick.triggerAttackRelease(shift('A2'), '16n', now);
            noise.start(now);
            noiseEnv.triggerAttackRelease('16n', now + 0.04);
            noise.stop(now + 0.2);
            await waitMs(380);
            setTimeout(() => {
                kick.dispose();
                noise.dispose();
                noiseEnv.dispose();
            }, 800);
            break;
        }
        case 'eau': {
            // Ondine — goutte cristalline : PluckSynth + delay long + lowpass
            const lp = new Tone.Filter({ type: 'lowpass', frequency: 2000 }).connect(output);
            const delay = new Tone.FeedbackDelay({ delayTime: 0.18, feedback: 0.4, wet: 0.35 }).connect(lp);
            const pluck = new Tone.PluckSynth({ attackNoise: 0.4, dampening: 3500, resonance: 0.75 }).connect(delay);
            pluck.triggerAttack(shift('B4'), now);
            await waitMs(560);
            setTimeout(() => {
                pluck.dispose();
                delay.dispose();
                lp.dispose();
            }, 1200);
            break;
        }
        case 'terre': {
            // Garek — tambour sourd : MembraneSynth low + reverb cave
            const reverb = new Tone.Reverb({ decay: 2.5, wet: 0.35 }).connect(output);
            const drum = new Tone.MembraneSynth({
                pitchDecay: 0.08,
                octaves: 3,
                envelope: { attack: 0.005, decay: 0.4, release: 0.2 },
            }).connect(reverb);
            drum.triggerAttackRelease(shift('C2'), '8n', now);
            await waitMs(520);
            setTimeout(() => {
                drum.dispose();
                reverb.dispose();
            }, 1500);
            break;
        }
        case 'foudre': {
            // Volta — crépitement bref : NoiseSynth + filter envelope court
            const filter = new Tone.Filter({ type: 'highpass', frequency: 1200 }).connect(output);
            const noise = new Tone.NoiseSynth({
                noise: { type: 'white' },
                envelope: { attack: 0.001, decay: 0.06, sustain: 0, release: 0.05 },
            }).connect(filter);
            noise.triggerAttackRelease('32n', now);
            // 2e crépitement bref pour le shimmer
            noise.triggerAttackRelease('64n', now + 0.07);
            await waitMs(220);
            setTimeout(() => {
                noise.dispose();
                filter.dispose();
            }, 600);
            break;
        }
        case 'lumiere': {
            // Sole — harpe arpège mineur 3 notes : PluckSynth, série harmonique D minor
            const pluck = new Tone.PluckSynth({ attackNoise: 0.3, dampening: 4500, resonance: 0.9 }).connect(output);
            const notes = ['D5', 'F5', 'A5'];
            notes.forEach((n, i) => pluck.triggerAttack(shift(n), now + i * 0.05));
            await waitMs(480);
            setTimeout(() => {
                pluck.dispose();
            }, 1000);
            break;
        }
    }
}
function waitMs(ms) {
    return new Promise((r) => setTimeout(r, ms));
}
//# sourceMappingURL=master-sounds.js.map